import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useServerTable, type AppColumns } from "../../../components/table/tableFeatures";
import { MdRotateLeft } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { Select } from "../../../components/fields/base/Select";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import {
  type ManagedTableState,
  type ManagedTableStateSetters,
} from "../../../components/table/usePersistedTableState";
import { resetTable } from "../../../components/table/utils";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { DataViewPicker } from "../../dataViews/components/DataViewPicker";
import { useGetSponsoredAdsAudienceBySegment } from "../api/audience/useSponsoredAdsAudienceDatasetsQuery";
import {
  type SponsoredAdsAudienceAdType,
  type SponsoredAdsAudienceGrouping,
  SPONSORED_ADS_AUDIENCE_GROUPING_OPTIONS,
  SPONSORED_ADS_AUDIENCE_AD_TYPE_OPTIONS,
  sponsoredAdsAudienceAdTypeSchema,
} from "./sponsoredAdsAudienceGroupings";
import { SponsoredAdsAudienceExpander } from "./SponsoredAdsAudienceExpander";
import { type SponsoredAdsAudienceRow } from "../../../zodios/apiDefinitions/api/sponsoredAdsAudience";

export function SponsoredAdsAudienceReportTable({
  state,
  initialState,
  setters,
  columns,
  grouping,
  onGroupingChange,
  adType,
  setAdType,
}: {
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  columns: AppColumns<SponsoredAdsAudienceRow>;
  grouping: SponsoredAdsAudienceGrouping;
  onGroupingChange: (grouping: string) => void;
  adType: SponsoredAdsAudienceAdType;
  setAdType: (adType: SponsoredAdsAudienceAdType) => void;
}) {
  const { t } = useTranslation(["perf_reports"]);
  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const groupingItems = useMemo(
    () => SPONSORED_ADS_AUDIENCE_GROUPING_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.label) })),
    [t],
  );
  const adTypeItems = useMemo(
    () => SPONSORED_ADS_AUDIENCE_AD_TYPE_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.label) })),
    [t],
  );

  const filters = useMemo(() => [...state.columnFilters], [state.columnFilters]);

  const dimensions = grouping.split(",");
  const getQueryOptions = useGetSponsoredAdsAudienceBySegment(dimensions, adType);
  const segmentQuery = useBlockWindowQuery(getQueryOptions, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters,
    search: debouncedSearch,
  });

  const table = useServerTable({
    data: segmentQuery.data?.data,
    columns,
    initialState,
    state,
    ...setters,
    getRowCanExpand: () => true,
    alternateSortingEnabled: true,
    rowCount: segmentQuery.data?.count ?? undefined,
    meta: {
      showSkeleton: segmentQuery.isFetching && (!segmentQuery.data || segmentQuery.isPlaceholderData),
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button variant={"contentOnly"} onPress={() => resetTable(table, "audienceReport")} fontSize={"xxlarge"}>
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <DataViewPicker viewKey="audienceReport" datasetKey="audience" table={table} />
            <SearchInput
              aria-label={t(($) => $.perf_reports["Search audience segments"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
            <Flex
              className={css({
                alignSelf: "end",
                width: "800px",
                marginBottom: "large",
                paddingX: "medium",
                gap: "xlarge",
              })}
            >
              <Select
                label={t(($) => $.perf_reports["Group By"])}
                value={grouping}
                onChange={(key) => {
                  if (key !== null) {
                    onGroupingChange(key.toString());
                  }
                }}
                items={groupingItems}
              />
              <Select
                label={t(($) => $.perf_reports["Ad Type"])}
                value={adType}
                onChange={(key) => {
                  const parsed = sponsoredAdsAudienceAdTypeSchema.safeParse(key);
                  if (parsed.success) {
                    setAdType(parsed.data);
                  }
                }}
                items={adTypeItems}
              />
            </Flex>
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => (
          <SponsoredAdsAudienceExpander row={row.original} grouping={grouping} adType={adType} />
        )}
      />
      {!segmentQuery.isFetching && (segmentQuery.data?.data.length ?? 0) === 0 && (
        <Flex
          justifyContent={"center"}
          alignItems={"center"}
          padding={"xlarge"}
          className={css({ color: "grey", fontSize: "medium" })}
        >
          {t(
            ($) => $.perf_reports["No audience performance data is available for the selected filters and date range."],
          )}
        </Flex>
      )}
    </>
  );
}
